import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Users, IndianRupee, Phone, Megaphone, Loader2, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function ContractorJobDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [apps, setApps] = useState([]);
  const [blastOpen, setBlastOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [locationOnly, setLocationOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [j, a] = await Promise.all([api.get(`/jobs/${id}`), api.get(`/jobs/${id}/applications`)]);
      setJob(j.data); setApps(a.data);
    } catch (e) { toast.error(formatError(e)); nav("/dashboard"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeJob = async () => {
    try { await api.patch(`/jobs/${id}`, { status: "closed" }); toast.success("Closed"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const loadPreview = async (locOnly) => {
    try {
      const { data } = await api.get(`/jobs/${id}/blast/preview`, { params: { location_only: locOnly } });
      setPreview(data);
    } catch (e) { toast.error(formatError(e)); }
  };

  const openBlast = async () => {
    setLocationOnly(false);
    setPreview(null);
    setBlastOpen(true);
    loadPreview(false);
  };

  const toggleLocationOnly = async (next) => {
    setLocationOnly(next);
    setPreview(null);
    loadPreview(next);
  };

  const sendBlast = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${id}/blast`, null, { params: { location_only: locationOnly } });
      toast.success(`Blast sent to ${data.count} matching worker${data.count === 1 ? "" : "s"}`);
      setBlastOpen(false);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  if (!job) return <div className="p-10 text-center text-sarthi-muted">Loading…</div>;

  const canBlast = job.status !== "closed";

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <button onClick={() => nav(-1)} className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-sarthi-muted"><ArrowLeft className="h-4 w-4"/> {t("back")}</button>

      <div className="sarthi-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase text-sarthi-primary">{t(`categories_names.${job.category}`)}</div>
            <h1 className="font-display text-3xl font-extrabold">{job.title}</h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm font-semibold text-sarthi-muted">
              <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4"/>{job.location}</span>
              <span className="inline-flex items-center gap-1"><IndianRupee className="h-4 w-4"/>₹{job.salary_min}–₹{job.salary_max}</span>
              <span className="inline-flex items-center gap-1"><Users className="h-4 w-4"/>{job.positions} {t("positions")}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`rounded-full px-3 py-1 text-xs font-bold ${job.status === "closed" ? "bg-stone-200 text-stone-700" : "bg-green-100 text-green-800"}`}>{t(job.status === "closed" ? "status_closed" : "status_open")}</div>
            {job.status !== "closed" && <button data-testid="close-job-btn" onClick={closeJob} className="sarthi-btn-outline px-4 py-2 text-sm">{t("close_job")}</button>}
          </div>
        </div>

        {canBlast && (
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-dashed border-sarthi-primary/40 bg-gradient-to-r from-orange-50 to-amber-50 p-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sarthi-primary text-white">
              <Megaphone className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-base font-extrabold text-sarthi-text">Urgent hiring? Blast to matching workers</div>
              <div className="text-sm font-medium text-sarthi-muted">Push a WhatsApp + SMS alert to every worker whose skills match this job.</div>
            </div>
            <Dialog open={blastOpen} onOpenChange={setBlastOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="blast-open-btn"
                  onClick={openBlast}
                  className="sarthi-btn-primary inline-flex items-center gap-2 px-5 py-3 text-white"
                >
                  <Zap className="h-4 w-4" /> Blast now
                </button>
              </DialogTrigger>
              <DialogContent data-testid="blast-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 font-display text-2xl font-extrabold">
                    <Megaphone className="h-6 w-6 text-sarthi-primary" /> Blast to matching workers
                  </DialogTitle>
                  <DialogDescription className="text-base font-medium text-sarthi-muted">
                    We'll send a WhatsApp alert (with SMS fallback) about this job to every worker whose skills include <span className="font-bold text-sarthi-text">{job.category}</span>.
                  </DialogDescription>
                </DialogHeader>

                <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border-2 border-sarthi-border bg-sarthi-accent/40 p-3">
                  <input
                    type="checkbox"
                    data-testid="blast-loc-only-toggle"
                    checked={locationOnly}
                    onChange={(e) => toggleLocationOnly(e.target.checked)}
                    className="h-5 w-5"
                  />
                  <span className="text-sm font-bold text-sarthi-text">Only workers in <span className="text-sarthi-primary">{job.location}</span></span>
                </label>

                <div className="mt-3 rounded-xl bg-sarthi-accent p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">Estimated reach</div>
                  {preview === null ? (
                    <div className="mt-1 flex items-center gap-2 text-sm text-sarthi-muted">
                      <Loader2 className="h-4 w-4 animate-spin" /> Calculating…
                    </div>
                  ) : (
                    <div data-testid="blast-preview-count" className="font-display text-3xl font-extrabold text-sarthi-text">
                      {preview.count} worker{preview.count === 1 ? "" : "s"}
                    </div>
                  )}
                </div>

                <DialogFooter className="mt-4 gap-2 sm:gap-3">
                  <button
                    data-testid="blast-cancel-btn"
                    onClick={() => setBlastOpen(false)}
                    className="sarthi-btn-outline flex-1 py-3"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="blast-confirm-btn"
                    onClick={sendBlast}
                    disabled={busy || preview === null || (preview && preview.count === 0)}
                    className="sarthi-btn-primary flex-1 py-3 text-white disabled:opacity-50"
                  >
                    {busy ? <><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Sending…</> : <>Send blast</>}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <h2 className="mt-8 font-display text-2xl font-extrabold">{t("applicants")} ({apps.length})</h2>
      {apps.length === 0 ? (
        <div className="sarthi-card mt-4 p-6 text-center text-sarthi-muted">No applicants yet.</div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {apps.map((a) => (
            <div key={a.id} data-testid={`applicant-${a.id}`} className="sarthi-card p-4">
              <div className="font-display text-lg font-extrabold">{a.worker_name}</div>
              <div className="mt-1 text-sm font-semibold text-sarthi-muted">Exp: {a.worker_experience} yr · Skills: {(a.worker_skills || []).join(", ")}</div>
              {a.worker_mobile && <div className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-sarthi-secondary"><Phone className="h-4 w-4"/> {a.worker_mobile}</div>}
              {a.message && <div className="mt-2 rounded-lg bg-sarthi-accent p-2 text-sm">{a.message}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
